import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';

import Text from '~components/atoms/Text';
import TabPageContainer from '~components/atoms/TabPageContainer';
import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';
import DownloadCSV from '~components/molecules/DownloadCSV';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import InventoryTransactionSearchAndFilterBar from './InventoryTransactionSearchAndFilterBar';
import InventoryTransactionTable from './InventoryTransactionTable';
import InventoryTransactionPaginator from './InventoryTransactionPaginator';
import ConfirmUpdateInventoryTransactionStatusModal from './ConfirmUpdateInventoryTransactionStatusModal';

import { FundTypeEnum, GoldTypeEnum, InventoryActionEnum, MetalTypeEnum, SilverTypeEnum, TransactionStatusEnum } from '~utils/enums';
import UtilFunctions from '~utils/functions';

import * as Styles from './_goldInventoryTransaction.module.scss';

const GoldInventoryTransaction = () => {
    const dispatch = useAppDispatch();

    const { Misc: { dateTimeFormat } } = UtilFunctions;

    const exportInventoryTransactionListing = () => { 
        isReadyToExportRef.current = true; 
        dispatch(combinedActions.goldExportInventoryTransactionListingAttempt()); 
    };
    const clearInventoryTransactionListingForExport = () => dispatch(combinedActions.goldClearInventoryTransactionListingForExport());

    const isReadyToExportRef = useRef(false);
    const prevLoadingRef = useRef(false);

    const inventoryTransactionListingIndex = useAppSelector((state) => state.gold.goldInventory.inventoryTransactionListing.index);

    const getInventoryTransactionListingForExportIsLoading = useAppSelector(state => state.gold.loadings.exportInventoryTransactionListing);
    const inventoryTransactionListingForExport = useAppSelector(state => state.gold.inventoryTransactionListingForExport);

    const [isExportingInventoryTransactionListing, setIsExportingInventoryTransactionListing] = useState(false);

    useEffect(() => {
        dispatch(combinedActions.goldGetInventoryTransactionListingAttempt());
    }, [inventoryTransactionListingIndex]);

    const listHeader = [
        'PRODUCT NAME',
        'TRANSACTION TYPE',
        'STATUS',
        'AMOUNT',
        'PHYSICAL WEIGHT',
        'PAPER WEIGHT (G)',
        'SERIAL NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        'TOTAL COST',
        'TOTAL PRICE',
        'FUND TYPE',
        'GOLD TYPE',
        'SILVER TYPE',
        'TRANSACTION DATE',
    ];

    const renderAction = (action: InventoryActionEnum) => {
        if (!action) return '-';

        if (action == InventoryActionEnum.TransferTo) return 'Transfer To';
        if (action == InventoryActionEnum.TransferFrom) return 'Transfer From';
        if (action == InventoryActionEnum.Restock) return 'Restock';
        if (action == InventoryActionEnum.NewProduct) return 'New Product';
        if (action == InventoryActionEnum.BankSell) return 'Bank Sell';
        if (action == InventoryActionEnum.BankBuy) return 'Bank Buy';
        if (action == InventoryActionEnum.RejectTransaction) return 'Reject Transaction';
        if (action == InventoryActionEnum.CancelTransaction) return 'Cancel Transaction';

        return InventoryActionEnum[action];
    };

    const listData = (inventoryTransactionListingForExport || []).map(transaction => {
        const {
            productName,
            code,
            branchCode,
            branchName,
            fundType,
            totalPrice,
            createdAt,
            amount,
            action,
            status,
            totalNetValue,
            metalType,
        } = transaction;

        const massagedCode = code.map(serial => serial.serialNo).filter(item => item).join('|');

        const physicalWeight =
            metalType === MetalTypeEnum.Gold
                ? `${transaction.physicalGoldWeight ?? 0} g`
                : `${transaction.physicalSilverWeight ?? 0} g`;

        const paperWeight =
            metalType === MetalTypeEnum.Gold
                ? `${transaction.paperGoldWeight ?? 0} g`
                : `${transaction.paperSilverWeight ?? 0} g`;

        const goldTypeDisplay =
            metalType === MetalTypeEnum.Gold && transaction.goldType
                ? GoldTypeEnum[transaction.goldType].split(/(?=[A-Z])/).join(' ')
                : '-';

        const silverTypeDisplay =
            metalType === MetalTypeEnum.Silver && transaction.silverType
                ? SilverTypeEnum[transaction.silverType].split(/(?=[A-Z])/).join(' ')
                : '-';

        return {
            productName: productName || '-',
            action: renderAction(action),
            status: status ? TransactionStatusEnum[status] : '-',
            amount: amount ?? '0',
            physicalWeight,
            paperWeight,
            code: massagedCode || '-',
            branchCode: branchCode || '-',
            branchName: branchName || '-',
            totalCost: typeof totalPrice === 'number'
                ? `RM${(Math.round(totalPrice * 100) / 100).toFixed(2)}`
                : '-',
            totalPrice: typeof totalNetValue === 'number'
                ? `RM${(Math.round(totalNetValue * 100) / 100).toFixed(2)}`
                : '-',
            fundType: fundType ? FundTypeEnum[fundType] : '-',
            goldType: goldTypeDisplay,
            silverType: silverTypeDisplay,
            createdAt: createdAt ? dateTimeFormat(createdAt) : '-',
        };
    });

    const handleExportInventoryTransactionListingCleanup = () => {
        clearInventoryTransactionListingForExport();
        setIsExportingInventoryTransactionListing(false);
        isReadyToExportRef.current = false;
    };

    useEffect(() => {
        if (prevLoadingRef.current && !getInventoryTransactionListingForExportIsLoading && isReadyToExportRef.current) {
            if (listData.length === 0) {
                const csv = listHeader.join(',') + '\r\n';
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${dayjs().format('DD-MM-YYYY')} Inventory Transaction Listing.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                handleExportInventoryTransactionListingCleanup();
            }
        }

        prevLoadingRef.current = getInventoryTransactionListingForExportIsLoading;
    }, [getInventoryTransactionListingForExportIsLoading]);

    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Inventory Transactions List'
                    className={Styles.title}
                    isBold
                />

                <DownloadCSV
                    buttonLabel='Export Inventory Transaction Listing to CSV'
                    header={listHeader}
                    data={listData}
                    fileName={dayjs().format('DD-MM-YYYY').concat(' Inventory Transaction Listing')}
                    onClick={exportInventoryTransactionListing}
                    cleanup={handleExportInventoryTransactionListingCleanup}
                    isLoading={getInventoryTransactionListingForExportIsLoading || isExportingInventoryTransactionListing}
                />

            </TabPageHeaderWrapper>

            <InventoryTransactionSearchAndFilterBar />

            <InventoryTransactionTable />

            <InventoryTransactionPaginator />

            <ConfirmUpdateInventoryTransactionStatusModal />
        </TabPageContainer>
    );
};

export default GoldInventoryTransaction;
